import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faCheck, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import { useTranslation } from 'react-i18next';

/**
 * ConfirmDialog
 *
 * A modal confirmation dialog used to request user confirmation for
 * potentially destructive actions. When `isOpen` is false the component
 * returns null and renders nothing.
 *
 * Props:
 * - isOpen: whether the dialog is visible
 * - title, message: text values shown to the user
 * - confirmLabel, cancelLabel, loadingLabel: button labels
 * - onConfirm, onCancel: callbacks for user actions
 * - confirmVariant: visual variant for the confirm button (danger/success/primary)
 * - loading: disables interactions and shows loadingLabel when true
 *
 * Returns: a centered modal with an icon, title, optional message and action buttons.
 */
const ConfirmDialog = ({
  isOpen,
  title,
  message,
  confirmLabel,
  cancelLabel,
  loadingLabel,
  onConfirm,
  onCancel,
  confirmVariant = 'danger',
  loading = false,
  confirmDisabled = false,
}) => {
  const { t } = useTranslation();
  const effectiveTitle = title ?? t('common.dialogs.confirm.title');
  const effectiveConfirmLabel = confirmLabel ?? t('common.dialogs.confirm.confirm');
  const effectiveCancelLabel = cancelLabel ?? t('common.dialogs.confirm.cancel');

  if (!isOpen) return null;

  // Resolve visual colors for the confirm button based on the chosen variant.
  // This small mapping centralizes how primary/danger/success variants look.
  const confirmStyles = {
    primary: {
      backgroundColor: UI_COLORS.BUTTONS.PRIMARY.background,
      hover: UI_COLORS.BUTTONS.PRIMARY.hover,
      text: UI_COLORS.BUTTONS.PRIMARY.text,
    },
    danger: {
      backgroundColor: UI_COLORS.BUTTONS.DANGER.background,
      hover: UI_COLORS.BUTTONS.DANGER.hover.background,
      text: UI_COLORS.BUTTONS.DANGER.text,
    },
    success: {
      backgroundColor: UI_COLORS.BUTTONS.SUCCESS.background,
      hover: UI_COLORS.BUTTONS.SUCCESS.hover.background,
      text: UI_COLORS.BUTTONS.SUCCESS.text,
    },
  }[confirmVariant] || {
    backgroundColor: UI_COLORS.BUTTONS.PRIMARY.background,
    hover: UI_COLORS.BUTTONS.PRIMARY.hover,
    text: UI_COLORS.BUTTONS.PRIMARY.text,
  };

  // Choose header icon and background color based on the same variant so the
  // dialog appearance matches the intended emotion (success vs danger).
  const headerConfig = {
    danger: { icon: faExclamationTriangle, color: UI_COLORS.STATUS.ERROR },
    success: { icon: faCheck, color: UI_COLORS.STATUS.SUCCESS },
    primary: { icon: faInfoCircle, color: UI_COLORS.BRAND.PRIMARY },
  }[confirmVariant] || { icon: faExclamationTriangle, color: UI_COLORS.STATUS.ERROR };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{ backgroundColor: UI_COLORS.BACKGROUNDS.OVERLAY }}
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center gap-4 border-b px-6 py-5" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
          <div
            className="flex h-12 w-12 items-center justify-center rounded-full"
            style={{ backgroundColor: colorHelpers.withOpacity(headerConfig.color, 0.1) }}
          >
            <FontAwesomeIcon icon={headerConfig.icon} style={{ color: headerConfig.color }} size="lg" />
          </div>
          <div>
            <h2 className="text-lg font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
              {effectiveTitle}
            </h2>
            {message && (
              // If message is a plain string render inside a paragraph; otherwise render the node directly
              typeof message === 'string' ? (
                <p className="mt-2 text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                  {message}
                </p>
              ) : (
                <div className="mt-2 text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                  {message}
                </div>
              )
            )}
          </div>
        </div>

        <div className={`flex items-center ${effectiveCancelLabel ? 'justify-end gap-3' : 'justify-center'} px-6 py-4`}>
          {effectiveCancelLabel && (
            <button
              type="button"
              onClick={onCancel}
              className="rounded-xl px-5 py-3 font-semibold transition-all duration-200 hover:scale-105"
              disabled={loading}
              style={{
                backgroundColor: 'transparent',
                color: UI_COLORS.TEXT.MUTED,
                border: `1px solid ${UI_COLORS.BORDERS.MEDIUM}`,
              }}
            >
              {effectiveCancelLabel}
            </button>
          )}

          <button
            type="button"
            onClick={onConfirm}
            className="rounded-xl px-5 py-3 font-semibold transition-all duration-200 hover:scale-105 disabled:cursor-not-allowed disabled:opacity-60"
            disabled={loading || confirmDisabled}

            style={{
              backgroundColor: confirmStyles.backgroundColor,
              color: confirmStyles.text,
              border: 'none',
            }}
            onMouseEnter={(event) => {
              if (!loading) {
                event.currentTarget.style.backgroundColor = confirmStyles.hover;
              }
            }}
            onMouseLeave={(event) => {
              event.currentTarget.style.backgroundColor = confirmStyles.backgroundColor;
            }}
          >
            {loading ? loadingLabel || effectiveConfirmLabel : effectiveConfirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialog;
